import type { ContentBlock, ContentDocument, Manifest } from '../content/data';
import { useProgress, useProgressActions } from '../progress/progress-context';
import { MarkdownView } from './markdown-view';

function displayMarkdown(block: ContentBlock): string {
  return block.markdown
    .split('\n')
    .filter((line) => !/^intended weekday:/i.test(line.trim()))
    .filter((line) => !/^\s*- \[[ xX]\]\s+/.test(line))
    .join('\n')
    .trim();
}

function ChecklistGroup({ document, blockId, readOnly }: { document: ContentDocument; blockId: string; readOnly: boolean }) {
  const progress = useProgress();
  const { setChecklist } = useProgressActions();
  const items = document.checklists.filter((item) => item.blockId === blockId);

  return (
    <fieldset className="checklist-group">
      <legend className="sr-only">Completion checklist</legend>
      {items.map((item) => (
        <label className="check-row" key={item.id}>
          <input
            type="checkbox"
            checked={progress.checklist[item.id] ?? false}
            disabled={readOnly}
            onChange={(event) => setChecklist(item.id, event.currentTarget.checked)}
          />
          <span>{item.text}</span>
        </label>
      ))}
    </fieldset>
  );
}

export function ContentDocumentView({
  document,
  manifest,
  excludeHeadings = [],
  excludeBlockIds = [],
  readOnly = false
}: {
  document: ContentDocument;
  manifest: Manifest;
  excludeHeadings?: string[];
  excludeBlockIds?: string[];
  readOnly?: boolean;
}) {
  const excludedHeadings = new Set(excludeHeadings.map((heading) => heading.toLowerCase()));
  const excludedBlocks = new Set(excludeBlockIds);

  return (
    <div className="document-flow">
      {document.blocks.map((block) => {
        const heading = block.heading?.toLowerCase() ?? '';
        if (excludedHeadings.has(heading) || excludedBlocks.has(block.id)) return null;

        const markdown = displayMarkdown(block);
        const hasChecklist = block.checklistIds.length > 0;
        if (!markdown && !hasChecklist) return null;

        return (
          <section className="lesson-block" key={block.id}>
            {markdown ? <MarkdownView markdown={markdown} document={document} manifest={manifest} /> : null}
            {hasChecklist ? <ChecklistGroup document={document} blockId={block.id} readOnly={readOnly} /> : null}
          </section>
        );
      })}
    </div>
  );
}
